import React, { useState } from "react";
import api from "../../api/api";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";

import LockResetIcon from "@mui/icons-material/LockReset";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { email, code } = location.state || {};

  const handleReset = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!email || !code) {
      setMessage(
        "Your password reset session is missing or expired. Please request a new verification code."
      );
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/set-new-password/", {
        email,
        code,
        new_password: newPassword,
      });

      setMessage(
        res.data.message ||
          "Password reset successful. Redirecting..."
      );

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Password reset failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background:
          "linear-gradient(135deg, #ffffff 0%, #faf7ff 52%, #f2eafb 100%)",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,

        "@keyframes float1": {
          "0%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-35px)" },
          "100%": { transform: "translateY(0px)" },
        },

        "@keyframes float2": {
          "0%": { transform: "translateY(0px) translateX(0px)" },
          "50%": {
            transform: "translateY(25px) translateX(-20px)",
          },
          "100%": {
            transform: "translateY(0px) translateX(0px)",
          },
        },

        "@keyframes float3": {
          "0%": { transform: "translateY(0px) translateX(0px)" },
          "50%": {
            transform: "translateY(-20px) translateX(20px)",
          },
          "100%": {
            transform: "translateY(0px) translateX(0px)",
          },
        },

        "@keyframes twinkle": {
          "0%": { opacity: 0.2 },
          "50%": { opacity: 0.85 },
          "100%": { opacity: 0.2 },
        },
      }}
    >
      {/* LARGE BUBBLES */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 180, md: 320 },
          height: { xs: 180, md: 320 },
          borderRadius: "50%",
          background: "rgba(84, 33, 143, 0.12)",
          top: "5%",
          left: "-5%",
          animation: "float1 14s ease-in-out infinite",
          filter: "blur(5px)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 220, md: 360 },
          height: { xs: 220, md: 360 },
          borderRadius: "50%",
          background: "rgba(244, 197, 66, 0.19)",
          top: "55%",
          right: "-10%",
          animation: "float2 18s ease-in-out infinite",
          filter: "blur(5px)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 170, md: 260 },
          height: { xs: 170, md: 260 },
          borderRadius: "50%",
          background: "rgba(103, 48, 165, 0.10)",
          bottom: "-5%",
          left: "25%",
          animation: "float3 20s ease-in-out infinite",
          filter: "blur(5px)",
          pointerEvents: "none",
        }}
      />

      {/* MEDIUM BUBBLES */}

      <Box
        sx={{
          position: "absolute",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(84, 33, 143, 0.08)",
          top: "15%",
          right: "15%",
          animation: "float2 12s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(244, 197, 66, 0.17)",
          bottom: "15%",
          left: "10%",
          animation: "float1 16s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 140,
          height: 140,
          borderRadius: "50%",
          background: "rgba(103, 48, 165, 0.09)",
          top: "40%",
          left: "8%",
          animation: "float3 15s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      {/* PURPLE AND GOLD STARS */}

      {[...Array(40)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: `${2 + (i % 3)}px`,
            height: `${2 + (i % 3)}px`,
            borderRadius: "50%",
            background:
              i % 3 === 0
                ? "#54218f"
                : i % 3 === 1
                ? "#f4c542"
                : "#9b77c5",
            top: `${(i * 13) % 100}%`,
            left: `${(i * 17) % 100}%`,
            opacity: 0.55,
            animation: `twinkle ${3 + (i % 4)}s infinite`,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* PASSWORD RESET CARD */}

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{
          width: "100%",
          maxWidth: "550px",
          zIndex: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "30px",
            background: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(84,33,143,0.15)",
            boxShadow:
              "0 20px 60px rgba(53,16,95,0.11)",

            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              backgroundColor: "#fdfbff",
              transition: "box-shadow 0.25s ease",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#e4d8f2",
            },

            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#9b77c5",
              },

            "& .MuiOutlinedInput-root.Mui-focused": {
              boxShadow: "0 0 0 3px rgba(84,33,143,0.08)",
            },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#54218f",
              },

            "& .MuiInputLabel-root.Mui-focused": {
              color: "#54218f",
            },
          }}
        >
          {/* HEADER */}

          <Box textAlign="center">
            <motion.div
              animate={{
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 3,
              }}
            >
              <LockResetIcon
                sx={{
                  fontSize: 80,
                  color: "#54218f",
                  filter:
                    "drop-shadow(0 5px 10px rgba(84,33,143,0.15))",
                }}
              />
            </motion.div>

            <Typography
              variant="h4"
              fontWeight={800}
              mt={2}
              sx={{
                color: "#35105f",
                letterSpacing: "-0.7px",
                fontSize: {
                  xs: "1.75rem",
                  sm: "2.125rem",
                },
              }}
            >
              Create New Password
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "#716b7d",
                fontSize: "0.95rem",
                lineHeight: 1.8,
              }}
            >
              Set a secure password for your School account.
            </Typography>
          </Box>

          {/* PASSWORD GUIDANCE */}

          <Alert
            severity="info"
            sx={{
              mt: 3,
              borderRadius: 3,
              backgroundColor: "#f5effc",
              color: "#4b286e",
              border: "1px solid #e7dcf4",

              "& .MuiAlert-icon": {
                color: "#54218f",
              },
            }}
          >
            Use at least 8 characters with a mix of letters,
            numbers, and symbols.
          </Alert>

          {/* FORM */}

          <Box
            component="form"
            onSubmit={handleReset}
            sx={{
              mt: 3,
            }}
          >
            <TextField
              fullWidth
              required
              label="New Password"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              disabled={loading}
              autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon
                      sx={{
                        color: "#54218f",
                      }}
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      aria-label={
                        showPassword
                          ? "Hide password"
                          : "Show password"
                      }
                      onClick={() =>
                        setShowPassword(!showPassword)
                      }
                      sx={{
                        color: "#54218f",

                        "&:hover": {
                          backgroundColor: "rgba(84,33,143,0.08)",
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              disabled={loading || !newPassword}
              sx={{
                mt: 3,
                py: 1.7,
                borderRadius: "14px",
                fontWeight: 700,
                textTransform: "none",
                fontSize: "1rem",
                background:
                  "linear-gradient(135deg, #54218f 0%, #6730a5 100%)",
                color: "#ffffff",
                boxShadow:
                  "0 8px 20px rgba(84,33,143,0.20)",
                transition: "all 0.25s ease",

                "&:hover": {
                  background:
                    "linear-gradient(135deg, #35105f 0%, #54218f 100%)",
                  boxShadow:
                    "0 12px 26px rgba(84,33,143,0.28)",
                  transform: "translateY(-1px)",
                },

                "&.Mui-disabled": {
                  background: "#c8b5df",
                  color: "#ffffff",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#ffffff",
                  }}
                />
              ) : (
                "Reset Password"
              )}
            </Button>

            <Button
              fullWidth
              type="button"
              startIcon={<ArrowBackIcon />}
              disabled={loading}
              onClick={() => navigate("/forgotPassword")}
              sx={{
                mt: 2,
                color: "#54218f",
                fontWeight: 650,
                textTransform: "none",
                borderRadius: "12px",
                py: 1.2,

                "&:hover": {
                  backgroundColor: "#f5effc",
                },
              }}
            >
              Back
            </Button>
          </Box>

          {/* STATUS MESSAGE */}

          {message && (
            <Alert
              severity={
                message.toLowerCase().includes("successful")
                  ? "success"
                  : "error"
              }
              sx={{
                mt: 3,
                borderRadius: 3,
                overflowWrap: "anywhere",

                ...(message.toLowerCase().includes("successful")
                  ? {
                      backgroundColor: "#eff8f0",
                      color: "#216e39",
                    }
                  : {
                      backgroundColor: "#fff2f1",
                    }),
              }}
            >
              {message}
            </Alert>
          )}

          {/* FOOTER */}

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #eee6f5",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: "#777184",
                fontSize: ".9rem",
              }}
            >
              Your School Name Here...
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default ResetPassword;
